<template>
    <div>
        <table class="table table-striped table-class hidden-sm hidden-xs">
            <thead>
                <tr>
                    <th
                        v-for="(column, idx) in columns"
                        :key="column.field || idx"
                        class="sortable-header"
                        @click="sortBy(column)"
                    >
                        <span v-if="sortField === column.field" class="sort-indicator">
                            <font-awesome-icon
                                v-if="sortDirection !== 'asc'"
                                :icon="['fas', 'chevron-up']"
                            />
                            <font-awesome-icon
                                v-if="sortDirection !== 'desc'"
                                :icon="['fas', 'chevron-down']"
                            />
                        </span>
                        {{ column.label }}
                    </th>
                </tr>
            </thead>
            <tbody v-if="paginatedRows.length > 0">
                <tr v-for="(row, rowIndex) in paginatedRows" :key="rowIndex">
                    <td v-for="(column, colIndex) in columns" :key="colIndex">
                        {{ formatValue(row[column.field], column) }}
                    </td>
                </tr>
            </tbody>
            <tbody v-else>
                <tr>
                    <td colspan="100%" class="text-center" style="padding: 20px">
                        No data available
                    </td>
                </tr>
            </tbody>
        </table>

        <div class="d-flex justify-content-between align-items-center my-3">
            <div>
                <label for="itemsPerPage" class="me-2">Items per page:</label>
                <select
                    id="itemsPerPage"
                    v-model.number="itemsPerPage"
                    @change="handleItemsPerPageChange"
                    class="form-select d-inline-block w-auto"
                >
                    <option v-for="option in [10, 20, 50, 100]" :key="option" :value="option">
                        {{ option }}
                    </option>
                </select>
            </div>

            <div>
                <button
                    class="btn btn-danger"
                    @click="changePage(currentPage - 1)"
                    :disabled="currentPage === 1 || totalPages === 0"
                >
                    Previous
                </button>
                <span class="mx-3">
                    Page <strong>{{ Math.min(currentPage, Math.max(totalPages, 1)) }}</strong> of
                    <strong>{{ Math.max(totalPages, 1) }}</strong>
                </span>
                <button
                    class="btn btn-danger"
                    @click="changePage(currentPage + 1)"
                    :disabled="currentPage === totalPages || totalPages === 0"
                >
                    Next
                </button>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        name: 'PartHistoryTable',
        props: {
            columns: {
                type: Array,
                required: true,
            },
            rows: {
                type: Array,
                default: () => [],
            },
            initialSort: {
                type: Object,
                default: () => ({
                    field: '',
                    direction: 'desc',
                }),
            },
        },
        data() {
            return {
                sortField: this.initialSort?.field || (this.columns?.[0]?.field ?? ''),
                sortDirection: this.initialSort?.direction || 'asc',
                currentPage: 1,
                itemsPerPage: 10,
            }
        },
        computed: {
            sortedRows() {
                if (!this.sortField) {
                    return this.rows.slice()
                }

                return this.rows.slice().sort((a, b) => {
                    const column = this.columns.find((col) => col.field === this.sortField) || {}
                    const valueA = this.normalizeValue(a[this.sortField], column)
                    const valueB = this.normalizeValue(b[this.sortField], column)

                    if (valueA < valueB) {
                        return this.sortDirection === 'asc' ? -1 : 1
                    }
                    if (valueA > valueB) {
                        return this.sortDirection === 'asc' ? 1 : -1
                    }
                    return 0
                })
            },
            paginatedRows() {
                const start = (this.currentPage - 1) * this.itemsPerPage
                const end = start + this.itemsPerPage
                return this.sortedRows.slice(start, end)
            },
            totalPages() {
                return Math.ceil(this.rows.length / this.itemsPerPage)
            },
        },
        watch: {
            rows() {
                this.currentPage = 1
            },
            columns(newColumns) {
                if (!this.sortField && newColumns.length > 0) {
                    this.sortField = newColumns[0].field
                }
            },
        },
        methods: {
            sortBy(column) {
                if (this.sortField === column.field) {
                    this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
                } else {
                    this.sortField = column.field
                    this.sortDirection = 'asc'
                }
            },
            changePage(page) {
                if (page > 0 && page <= this.totalPages) {
                    this.currentPage = page
                }
            },
            handleItemsPerPageChange() {
                this.currentPage = 1
            },
            formatValue(value, column) {
                if (column?.formatFn && typeof column.formatFn === 'function') {
                    try {
                        return column.formatFn(value)
                    } catch (error) {
                        console.error('Failed to format value', error)
                        return value
                    }
                }
                return value
            },
            normalizeValue(value, column = {}) {
                if (column.type === 'number') {
                    const numeric = Number(
                        typeof value === 'string' ? value.replace(/[^0-9.-]+/g, '') : value
                    )
                    return isNaN(numeric) ? 0 : numeric
                }

                if (value instanceof Date) {
                    return value.getTime()
                }

                if (typeof value === 'string') {
                    const date = new Date(value)
                    if (!isNaN(date)) {
                        return date.getTime()
                    }
                    return value.toLowerCase()
                }

                return value ?? ''
            },
        },
    }
</script>

<style scoped>
    .sortable-header {
        cursor: pointer;
    }

    .sort-indicator {
        margin-right: 6px;
    }
</style>
